import React from 'react';
import { FormikProps } from 'formik';
import * as Yup from 'yup';
import { FormField } from '../../../../../../global/components/form-element/form-field';
import { FormGroup } from '../../../../../../global/components/layout/form-group';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';
import { YesNoOptions } from '../../../../../../global/constants/lists';
import { YesNoIndicators } from '../../../../../../global/models/api-model';
import { QccoStatus } from '../models/plan-adoption-form-models';

function handleQccoQuestionChange<T extends { qccoQuestion1: string; qccoQuestion2: string; qccoQuestion3: string }>(
    formProps: FormikProps<T>, 
    questionName: string, 
    value: any,
): void {
    const actualValue = value?.target?.value || value;
    const newValues = { ...formProps.values };
    
    if (questionName === 'qccoQuestion1') {
        if (actualValue === YesNoIndicators.Yes) {
            // If Q1 = Yes, Q2 and Q3 are N/A
            newValues.qccoQuestion2 = YesNoIndicators.NotApplicable;
            newValues.qccoQuestion3 = YesNoIndicators.NotApplicable;
        } else if (actualValue === YesNoIndicators.No) {
            newValues.qccoQuestion2 = '';
            newValues.qccoQuestion3 = '';
        }
    } else if (questionName === 'qccoQuestion2') {
        if (actualValue === YesNoIndicators.No) {
            // If Q2 = No, Q3 is N/A
            newValues.qccoQuestion3 = YesNoIndicators.NotApplicable;
        } else if (actualValue === YesNoIndicators.Yes) {
            newValues.qccoQuestion3 = '';
        }
    }
    
    newValues[questionName] = actualValue;
    formProps.setValues(newValues);
}

/**
 * Dynamic QCCO Status Calculation:
 * - If Q1 = "Yes": Qualified (Q2 and Q3 not displayed)
 * - If Q1 = "No" and Q2 = "No": Qualified (Q3 not displayed)
 * - If Q1 = "No" and Q2 = "Yes" and Q3 = "Yes": Not Qualified
 * - If Q1 = "No" and Q2 = "Yes" and Q3 = "No": Qualified
 * - If required questions are not answered, return null
 */
export function calculateQccoStatus(
    qccoQuestion1: string, 
    qccoQuestion2: string, 
    qccoQuestion3: string,
): QccoStatus {
    // Question 1 is always required
    if (!qccoQuestion1 || qccoQuestion1 === YesNoIndicators.NotApplicable) return null;

    // If Q1 = "Yes", QCCO STATUS: YES
    if (qccoQuestion1 === YesNoIndicators.Yes) {
        return QccoStatus.Qualified;
    }

    // If Q1 = "No", need Q2
    if (qccoQuestion1 === YesNoIndicators.No) {
        if (!qccoQuestion2 || qccoQuestion2 === YesNoIndicators.NotApplicable) return null;

        // If Q2 = "No", QCCO STATUS: YES
        if (qccoQuestion2 === YesNoIndicators.No) {
            return QccoStatus.Qualified;
        }

        // If Q2 = "Yes", need Q3
        if (qccoQuestion2 === YesNoIndicators.Yes) {
            if (!qccoQuestion3 || qccoQuestion3 === YesNoIndicators.NotApplicable) return null;

            // If Q3 = "Yes", QCCO STATUS: NO
            if (qccoQuestion3 === YesNoIndicators.Yes) {
                return QccoStatus.NotQualified;
            }

            // If Q3 = "No", QCCO STATUS: YES
            if (qccoQuestion3 === YesNoIndicators.No) {
                return QccoStatus.Qualified;
            }
        }
    }

    return null;
}


export function mapQccoQuestionValue(questionValue: string): string {
    if (questionValue === YesNoIndicators.Yes) return 'Y';
    if (questionValue === YesNoIndicators.NotApplicable) return '';
    return 'N';
}

/**
 * Maps API QCCO values back to UI form values
 * empty string = NotApplicable
 */
export function mapApiQccoValueToForm(apiValue: string): string {
    if (apiValue === 'Y') return YesNoIndicators.Yes;
    if (apiValue === 'N') return YesNoIndicators.No;
    if (apiValue === '' || apiValue === null || apiValue === undefined) return YesNoIndicators.NotApplicable;
    return apiValue;
}

/**
 * Creates QCCO question validation schema
 */
export function createQccoValidationSchema() {
    return {
        qccoQuestion1: Yup.string().required('Question 1 is required'),
        qccoQuestion2: Yup.string().when('qccoQuestion1', {
            is: 'N',
            then: Yup.string().required('Question 2 is required'),
            otherwise: Yup.string().nullable(),
        }),
        qccoQuestion3: Yup.string().when(['qccoQuestion1', 'qccoQuestion2'], {
            is: (q1, q2) => q1 === 'N' && q2 === 'Y',
            then: Yup.string().required('Question 3 is required'),
            otherwise: Yup.string().nullable(),
        }),
    };
}

interface QccoQuestionsSharedProps<T> {
    formProps: FormikProps<T>;
    content: {
        qccoQuestionTitle?: string;
        qccoQuestion1Label?: string;
        qccoQuestion2Label?: string;
        qccoQuestion3Label?: string;
        qccoQualificationStatusTitle?: string;
        qccoQualificationStatusText?: string;
        qccoQualificationYesStatusTitle?: string;
        qccoQualificationYesStatusText?: string;
        qccoQualificationNoStatusTitle?: string;
        qccoQualificationNoStatusText?: string;
    };
    showStatus?: boolean;
    disabledOverlay?: boolean;
}

export function QccoQuestionsShared<T extends { qccoQuestion1: string; qccoQuestion2: string; qccoQuestion3: string }>({
    formProps,
    content,
    showStatus = true,
    disabledOverlay,
}: QccoQuestionsSharedProps<T>) {
    if (!content?.qccoQuestionTitle) return null;

    function renderQualificationStatus(values: T) {
        const qccoCalculatedStatus = calculateQccoStatus(values.qccoQuestion1, values.qccoQuestion2, values.qccoQuestion3);

        if (!qccoCalculatedStatus) return null;

        if (qccoCalculatedStatus === QccoStatus.Qualified) {
            return (
                <>
                    <h4>{content?.qccoQualificationYesStatusTitle}</h4>
                    <WysiwygWithReactLink content={content?.qccoQualificationYesStatusText} />
                </>
            );
        }

        if (qccoCalculatedStatus === QccoStatus.NotQualified) {
            return (
                <>
                    <h4>{content?.qccoQualificationNoStatusTitle}</h4>
                    <WysiwygWithReactLink content={content?.qccoQualificationNoStatusText} />
                </>
            );
        }
        return null;
    }

    return (
        <DividerSection sectionText={content?.qccoQuestionTitle} removeDivider disabledOverlay={disabledOverlay}>
            <FormGroup>
                <FormField
                    name='qccoQuestion1'
                    label={content?.qccoQuestion1Label}
                    component="radioGroup"
                    options={YesNoOptions}
                    touched={formProps.touched}
                    errors={formProps.errors}
                    required
                    onChange={(value: string) => handleQccoQuestionChange(formProps, 'qccoQuestion1', value)}
                />

                {formProps.values.qccoQuestion1 === YesNoIndicators.No && (
                    <FormField
                        name='qccoQuestion2'
                        label={content?.qccoQuestion2Label}
                        component="radioGroup"
                        options={YesNoOptions}
                        touched={formProps.touched}
                        errors={formProps.errors}
                        required
                        onChange={(value: string) => handleQccoQuestionChange(formProps, 'qccoQuestion2', value)}
                    />
                )}

                {formProps.values.qccoQuestion1 === YesNoIndicators.No && 
                 formProps.values.qccoQuestion2 === YesNoIndicators.Yes && (
                    <FormField
                        name='qccoQuestion3'
                        label={content?.qccoQuestion3Label}
                        component="radioGroup"
                        options={YesNoOptions}
                        touched={formProps.touched}
                        errors={formProps.errors}
                        required
                        onChange={(value: string) => handleQccoQuestionChange(formProps, 'qccoQuestion3', value)}
                    />
                )}
            </FormGroup>
            
            {showStatus && (
                <>
                    <ContentSpacer>
                    </ContentSpacer>
                    <ContentSpacer>
                        <ContentSpacer>
                            <h3>{content?.qccoQualificationStatusTitle}</h3>
                        </ContentSpacer>
                        {renderQualificationStatus(formProps.values)}
                        <WysiwygWithReactLink content={content?.qccoQualificationStatusText} />
                    </ContentSpacer>
                </>
            )}
        </DividerSection>
    );
}

QccoQuestionsShared.defaultProps = {
    showStatus: true,
    disabledOverlay: false,
};
